import { api } from '../client';

export interface ConsumptionRecord {
  id: string;
  companyId: string;
  itemId: string;
  item: {
    id: string;
    name: string;
    sku: string;
    category: string;
    unit: string;
  };
  locationId: string;
  location: {
    id: string;
    name: string;
    code: string;
  };
  workOrderId: string | null;
  workOrder: {
    id: string;
    workOrderNumber: string;
    title: string;
  } | null;
  quantity: number;
  unit: string;

  // OFS-specific: Variance tracking
  plannedQuantity: number | null;
  varianceReason: string | null;
  variancePercentage: number | null;

  // OFS-specific: Approval workflow
  approvalStatus: string;
  approvedById: string | null;
  approvedBy: {
    id: string;
    fullName: string;
  } | null;
  approvedAt: string | null;
  requiresApproval: boolean;

  // OFS-specific: Operation context
  jobId: string | null;
  jobRefId?: string | null;
  /** Material brought back against this record; net is quantity minus this. */
  returnedQuantity?: number;
  returnedAt?: string | null;
  returnReason?: string | null;
  jobName: string | null;
  operationPhase: string | null;

  // OFS-specific: Rate-based consumption
  consumptionRate: number | null;
  rateUnit: string | null;
  baseMetric: number | null;

  // OFS-specific: Cost tracking
  unitCost: number | null;
  totalCost: number | null;

  // OFS-specific: Certification
  certificateNumbers: string[];
  requiresCertification: boolean;

  // Audit trail
  consumedAt: string;
  consumedById: string;
  consumedBy: {
    id: string;
    fullName: string;
    email: string;
  };
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  deletedBy: string | null;
}

export interface CreateConsumptionDto {
  // Core fields
  itemId: string;
  locationId: string;
  quantity: number;
  unit: string;
  workOrderId?: string;

  // OFS-specific: Variance tracking
  plannedQuantity?: number;
  varianceReason?: string;

  // OFS-specific: Operation context
  /** Superseded by jobRefId; kept for existing integrations. */
  jobId?: string;
  /** The job this material is charged to. */
  jobRefId?: string;
  jobName?: string;
  operationPhase?: string;

  // OFS-specific: Rate-based consumption
  consumptionRate?: number;
  rateUnit?: string;
  baseMetric?: number;

  // OFS-specific: Certification
  certificateNumbers?: string[];

  // Audit fields
  notes?: string;
  consumedAt?: string;

  // Batch tracking
  batchIds?: string[];
}

export interface QueryConsumptionParams {
  itemId?: string;
  locationId?: string;
  workOrderId?: string;
  consumedById?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export type ConsumptionGroupBy = 'ITEM' | 'LOCATION' | 'DATE' | 'WORK_ORDER';

export interface ConsumptionSummaryParams {
  groupBy?: ConsumptionGroupBy;
  startDate?: string;
  endDate?: string;
  locationId?: string;
  itemId?: string;
}

export interface ConsumptionTrendsParams {
  itemId?: string;
  locationId?: string;
  interval?: 'daily' | 'weekly' | 'monthly';
  periods?: number;
}

export interface ConsumptionDashboard {
  /** The rolling window the summary figures are measured over. */
  periodDays?: number;
  /** Most recent consumption of any age — makes an empty window explicable. */
  lastRecordedAt?: string | null;
  summary: {
    today: { quantity: number; count: number };
    week: { quantity: number; count: number };
    month: { quantity: number; count: number };
  };
  topItems: {
    itemId: string;
    name: string;
    sku: string;
    unit: string;
    quantity: number;
  }[];
  topLocations: {
    locationId: string;
    name: string;
    code: string;
    quantity: number;
  }[];
}

export interface ConsumptionSummaryItem {
  itemId?: string;
  itemName?: string;
  itemSku?: string;
  unit?: string;
  locationId?: string;
  locationName?: string;
  locationCode?: string;
  workOrderId?: string;
  workOrderNumber?: string;
  workOrderTitle?: string;
  date?: string;
  totalQuantity: number;
  recordCount: number;
}

export interface ConsumptionTrendItem {
  period: string;
  quantity: number;
  count: number;
}

// OFS-specific: Variance Analysis
export interface VarianceAnalysisParams {
  startDate?: string;
  endDate?: string;
  itemId?: string;
  locationId?: string;
  threshold?: number;
}

export interface VarianceAnalysisRecord extends ConsumptionRecord {
  // All fields from ConsumptionRecord are included
}

export interface VarianceAnalysisResponse {
  records: VarianceAnalysisRecord[];
  summary: {
    totalPlanned: number;
    totalActual: number;
    totalVariance: number;
    averageVariancePercentage: number;
    totalCostVariance: number;
    recordsWithVariance: number;
    positiveVariances: number;
    negativeVariances: number;
  };
}

// OFS-specific: Consumption Forecast
//
// The forecast projects demand from the work that is actually planned — the
// footage a rig will drill, the stages a spread will pump — against the rate
// this item has been observed to consume per unit of that work. It falls back
// to a calendar average only when there is no planned work it can measure
// against, and says which it did.

/** One planned or active job, and the demand it implies for this item. */
export interface ForecastJobLine {
  jobId: string;
  jobNumber: string;
  jobName: string;
  wellName: string | null;
  startDate: string | null;
  plannedWork: number;
  workUnit: string;
  rateUsed: number;
  /** Where the rate came from — a matching phase, or pooled across all. */
  rateBasis: string;
  expectedQuantity: number;
}

/**
 * A job the forecast could not project, and why.
 *
 * Worth as much as the total: a job left out for want of a comparable rate is
 * a gap in the record, not an absence of demand.
 */
export interface ForecastExcludedJob {
  jobNumber: string;
  wellName: string | null;
  reason: string;
}

export interface ForecastResponse {
  item: { id: string; name: string; sku: string; unit: string };
  method: 'PLANNED_ACTIVITY' | 'HISTORICAL_AVERAGE' | 'INSUFFICIENT_DATA';
  horizonDays: number;
  basis: string;
  expectedQuantity: number | null;
  dailyAverage?: number;
  jobs: ForecastJobLine[];
  excludedJobs: ForecastExcludedJob[];
}

interface ConsumptionPaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const consumptionApi = {
  async recordReturn(id: string, data: RecordReturnDto) {
    const response = await api.post<ConsumptionRecord>(`/consumption/${id}/return`, data);
    return response.data;
  },

  getAll: (params?: QueryConsumptionParams) =>
    api.get<ConsumptionPaginatedResponse<ConsumptionRecord>>('/consumption', { params }),

  getById: (id: string) => api.get<ConsumptionRecord>(`/consumption/${id}`),

  getDashboard: () => api.get<ConsumptionDashboard>('/consumption/dashboard'),

  getSummary: (params?: ConsumptionSummaryParams) =>
    api.get<ConsumptionSummaryItem[]>('/consumption/summary', { params }),

  getTrends: (params?: ConsumptionTrendsParams) =>
    api.get<ConsumptionTrendItem[]>('/consumption/trends', { params }),

  record: (data: CreateConsumptionDto) => api.post<ConsumptionRecord>('/consumption', data),

  recordBulk: (items: CreateConsumptionDto[]) =>
    api.post<{
      success: number;
      failed: number;
      results: ConsumptionRecord[];
      errors: { item: CreateConsumptionDto; error: string }[];
    }>('/consumption/bulk', { items }),

  delete: (id: string) => api.delete<{ success: boolean }>(`/consumption/${id}`),

  // OFS-specific: Variance Analysis
  getVarianceAnalysis: (params?: VarianceAnalysisParams) =>
    api.get<VarianceAnalysisResponse>('/consumption/variance/analysis', { params }),

  // OFS-specific: Consumption Forecast
  getForecast: (itemId: string, periods?: number) =>
    api.get<ForecastResponse>(`/consumption/forecast/${itemId}`, {
      params: { periods },
    }),

  // OFS-specific: Approval Workflow
  getPendingApprovals: () => api.get<ConsumptionRecord[]>('/consumption/approvals/pending'),

  approve: (id: string) => api.patch<ConsumptionRecord>(`/consumption/${id}/approve`),

  reject: (id: string, reason: string) =>
    api.patch<ConsumptionRecord>(`/consumption/${id}/reject`, { reason }),
};

/** Consumption expressed per unit of work — the figure that travels between wells. */
export interface RecordReturnDto {
  quantity: number;
  locationId?: string;
  reason?: string;
  returnedAt?: string;
}

/** Consumption expressed per unit of work — the figure that travels between wells. */
export interface RateBenchmark {
  itemId: string;
  sku: string;
  itemName: string;
  operationPhase: string;
  rateUnit: string | null;
  unit: string;
  observations: number;
  averageRate: number;
  minRate: number;
  maxRate: number;
  /** False when built on too few observations to mean anything. */
  reliable: boolean;
}

export interface RateOutlier {
  recordId: string;
  sku: string;
  itemName: string;
  operationPhase: string;
  rate: number;
  averageRate: number;
  deviationPercent: number;
  rateUnit: string | null;
  unit: string;
  quantity: number;
  baseMetric: number | null;
  consumedAt: string;
  job: { id: string; jobNumber: string; name: string; wellName: string | null } | null;
}

export interface ConsumptionRates {
  periodDays: number;
  recordsWithRate: number;
  benchmarks: RateBenchmark[];
  outliers: RateOutlier[];
  coverage: { withRate: number; total: number; percent: number };
}

export async function getConsumptionForecast(itemId: string, periods = 30) {
  const response = await api.get<ForecastResponse>(`/consumption/forecast/${itemId}`, {
    params: { periods },
  });
  return response.data;
}

export async function getConsumptionRates(days = 365) {
  const response = await api.get<ConsumptionRates>('/consumption/rates', { params: { days } });
  return response.data;
}
